import {Provider} from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Apollo, APOLLO_OPTIONS } from "apollo-angular";
import {
  ApolloClientOptions,
  InMemoryCache,
  ApolloLink,
} from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { setContext } from '@apollo/client/link/context';
import { AuthService } from './auth/auth.service';
import { EnvironmentService } from './environment/environment.service';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { AlertService } from './alert/alert.service';
import { MissingDataService } from './services/missing-data.service';
import { finalize } from 'rxjs';
import { ProvidedRequiredArgumentsRule } from "graphql/validation";

export function apolloOptionsFactory(
  httpLink: HttpLink,
  auth: AuthService,
  env: EnvironmentService,
  alert: AlertService,
  missingData: MissingDataService
): ApolloClientOptions {
  //Apollo Error Handler
  const errorLink = new ErrorLink(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, extensions, path, locations }) => {
        if (
          message === 'Unauthenticated.' || (extensions?.hasOwnProperty('category') &&
          (extensions?.['category'] === 'auth' ||
            extensions?.['category'] === 'authentication'))
        ) {
          auth.logout(true);
        } else if (extensions?.hasOwnProperty('missingData')) {
          missingData.setData(extensions?.['missingData']);
        } else if (message.indexOf('Duplicate entry') > -1) {
          alert.add(
            'GraphQL',
            'Duplicate entries detected. Try refreshing the page.'
          );
        } else {
          if (message) {
            alert.add('GraphQL', message);
          } else {
            alert.add('GraphQL', 'Unknown Error.');
          }
        }
      });
    } else {
      alert.add('Network', 'Network Error.');
    }
  });

  const uri = env.api_url + '/graphql';

  const loadingLink = new ApolloLink((operation, forward) => {
    auth.call_running += 1;
    return forward(operation).pipe(
      finalize(() => {
        auth.call_running -= 1;
      })
    );
  });

  const basic = setContext((operation: any, context: any) => {
    return {
      headers: new HttpHeaders({
        Accept: 'charset=utf-8',
        'X-CREAWeb-Agent': '89c5c30498c2989611f9044be006197c',
        Authorization: auth.getToken() ? auth.getToken() : '',
      }),
    };
  });

  const link = ApolloLink.from([
    loadingLink,
    errorLink,
    basic,
    httpLink.create({ uri }),
  ]);
  const cache = new InMemoryCache();
  return {
    link,
    cache,
  };
}

export default function provideApolloClient(): Provider[]
{
  return [
    Apollo,
    HttpLink,
    {
      provide: APOLLO_OPTIONS,
      useFactory: apolloOptionsFactory,
      deps: [
        HttpLink,
        AuthService,
        EnvironmentService,
        AlertService,
        MissingDataService,
      ],
    },
  ]
}
